import { useEffect, useRef, useState } from 'react'
import { CarouselItem } from './HeroCarousel'

const MAX_IMAGE_WIDTH = 1440
const MAX_IMAGE_HEIGHT = 570

type UseHeroCarouselSlideParams = {
  extendedItems: ReadonlyArray<CarouselItem>
}

const useHeroCarouselSlide = ({ extendedItems }: UseHeroCarouselSlideParams) => {
  const [blockClick, setBlockClick] = useState(false) // block too fast events of scroll or swipe to give time to continuous scroll reset
  const [currentIndex, setCurrentIndex] = useState(1) // starts from 1 because 0 and the last are "duplicated" to the continuous scroll
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true)
  const [containerDimensions, setContainerDimensions] = useState({
    height: MAX_IMAGE_HEIGHT,
    width: MAX_IMAGE_WIDTH,
  })

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isHoveredRef = useRef(false)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)

  const startAutoSlide = () => {
    stopAutoSlide()
    intervalRef.current = setInterval(() => {
      if (!isHoveredRef.current) {
        goToNextSlide()
      }
    }, 7000)
  }

  const stopAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
  }

  const goToNextSlide = () => {
    if (!blockClick) {
      setIsTransitionEnabled(true)
      setCurrentIndex((prev) => prev + 1)
    }
  }

  const goToPrevSlide = () => {
    if (!blockClick) {
      setIsTransitionEnabled(true)
      setCurrentIndex((prev) => prev - 1)
    }
  }

  const handleMouseEnter = () => {
    isHoveredRef.current = true
    stopAutoSlide()
  }

  const handleMouseLeave = () => {
    isHoveredRef.current = false
    startAutoSlide()
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!blockClick) {
      touchStartX.current = e.touches[0].clientX
    }
    stopAutoSlide()
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!blockClick) {
      touchEndX.current = e.touches[0].clientX
    }
  }

  const handleTouchEnd = () => {
    if (!blockClick) {
      if (touchStartX.current !== null && touchEndX.current !== null) {
        const diff = touchStartX.current - touchEndX.current
        const swipeThreshold = 50 // Minimum px to consider as swipe

        if (diff > swipeThreshold) {
          goToNextSlide()
        } else if (diff < -swipeThreshold) {
          goToPrevSlide()
        }
      }

      touchStartX.current = null
      touchEndX.current = null
    }
    startAutoSlide()
  }

  useEffect(() => {
    if (currentIndex === extendedItems.length - 1) {
      setBlockClick(true)
      // scrolling to cloned first item, reset to first real item
      const timeout = setTimeout(() => {
        setIsTransitionEnabled(false)
        setCurrentIndex(1)
        setBlockClick(false)
      }, 500)

      return () => clearTimeout(timeout)
    }

    if (currentIndex === 0) {
      setBlockClick(true)
      // scrolling to cloned last item, reset to last real item
      const timeout = setTimeout(() => {
        setIsTransitionEnabled(false)
        setCurrentIndex(extendedItems.length - 2) // -2 to get the real index of the last item not considering the "duplicated" first and last to continuous scroll
        setBlockClick(false)
      }, 500)

      return () => clearTimeout(timeout)
    }
  }, [currentIndex, extendedItems.length])

  useEffect(() => {
    const updateDimensions = () => {
      if (imageRef.current) {
        setContainerDimensions({
          height: imageRef.current.offsetHeight,
          width: imageRef.current.offsetWidth,
        })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    startAutoSlide()

    return () => {
      window.removeEventListener('resize', updateDimensions)
      stopAutoSlide()
    }
  }, [])

  return {
    currentIndex,
    containerDimensions,
    imageRef,
    handlePrev: goToPrevSlide,
    handleNext: goToNextSlide,
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isTransitionEnabled,
  }
}

export default useHeroCarouselSlide
